/**
 * Notification Helper für Desktop-Benachrichtigungen
 * Wrapper für Web Notifications API
 */

class NotificationHelper {
  constructor() {
    this.isSupported = 'Notification' in window;
  }

  /**
   * Prüft ob Browser Notifications unterstützt
   */
  isNotificationSupported() {
    return this.isSupported;
  }

  /**
   * Gibt aktuellen Permission-Status zurück
   * @returns {string} 'granted', 'denied', 'default'
   */
  getPermissionStatus() {
    if (!this.isSupported) return 'denied';
    return Notification.permission;
  }

  /**
   * Fordert Notification-Permission vom User an
   * @returns {Promise<string>} Permission status
   */
  async requestPermission() {
    if (!this.isSupported) {
      console.warn('Notifications not supported in this browser');
      return 'denied';
    }

    if (Notification.permission === 'granted') {
      return 'granted';
    }

    if (Notification.permission === 'denied') {
      return 'denied';
    }

    try {
      const permission = await Notification.requestPermission();
      console.log('[NOTIFICATIONS] Permission result:', permission);
      return permission;
    } catch (error) {
      console.error('[NOTIFICATIONS] Permission request failed:', error);
      return 'denied';
    }
  }

  /**
   * Zeigt eine Desktop-Benachrichtigung
   * @param {Object} options Notification options
   * @param {string} options.title Notification title
   * @param {string} options.body Notification body
   * @param {string} options.icon Icon URL
   * @param {string} options.tag Unique tag für Notification
   * @param {Function} options.onClick Click handler
   * @returns {Notification|null} Notification instance oder null
   */
  showNotification({ title, body, icon, tag, onClick }) {
    console.log('[NOTIFICATIONS] showNotification called:', { title, body, icon, tag });

    if (!this.isSupported) {
      console.warn('[NOTIFICATIONS] Notifications not supported');
      return null;
    }

    console.log('[NOTIFICATIONS] Notification.permission:', Notification.permission);

    if (Notification.permission !== 'granted') {
      console.warn('[NOTIFICATIONS] Notification permission not granted, current:', Notification.permission);
      return null;
    }

    try {
      console.log('[NOTIFICATIONS] Creating notification...');
      const notification = new Notification(title, {
        body,
        icon: icon || '/redorbit_logo.png',
        tag: tag || 'chat-notification',
        badge: '/redorbit_logo.png',
        requireInteraction: true, // Bleibt sichtbar bis User interagiert
        silent: false
      });

      console.log('[NOTIFICATIONS] Notification created successfully:', notification);

      // Event handlers für Debugging
      notification.onshow = () => {
        console.log('[NOTIFICATIONS] Notification shown!');
      };

      notification.onerror = (error) => {
        console.error('[NOTIFICATIONS] Notification error:', error);
      };

      notification.onclose = () => {
        console.log('[NOTIFICATIONS] Notification closed');
      };

      // Click-Handler
      if (onClick) {
        notification.onclick = (event) => {
          console.log('[NOTIFICATIONS] Notification clicked');
          event.preventDefault();
          window.focus();
          onClick();
          notification.close();
        };
      }

      return notification;
    } catch (error) {
      console.error('[NOTIFICATIONS] Failed to show notification:', error);
      return null;
    }
  }

  /**
   * Zeigt eine Chat-Nachricht-Benachrichtigung
   * @param {Object} options
   * @param {string} options.senderName Name des Absenders
   * @param {string} options.message Nachricht
   * @param {Function} options.onClick Click handler
   */
  showChatNotification({ senderName, message, onClick }) {
    // Kürze die Nachricht wenn zu lang
    const shortMessage = message.length > 100
      ? message.substring(0, 100) + '...'
      : message;

    return this.showNotification({
      title: `Neue Nachricht von ${senderName}`,
      body: shortMessage,
      tag: `chat-${senderName}`, // Gruppiert Notifications vom selben User
      onClick
    });
  }
}

// Singleton Export
const notificationHelper = new NotificationHelper();
export default notificationHelper;
